import { FC, ReactNode, useState } from 'react';

import DownloadLogo from 'shared/assets/svg-icons/download.svg';
import SheetsLogo from 'shared/assets/svg-icons/sheets.svg';
import { Text, Dropdown, openNotification } from 'shared/components/ui';
import { ExportEnum, TopIdsWithFiltersCandidates } from 'shared/generated-models';
import { useModalState } from 'shared/hooks/use-modal-state';
import { TechnographicsExportCsvModal, ExportGoogleSheetsModal } from 'features/csv-export';
import { ContactUsModal, UpgradeModal } from 'features/auth/components';
import { useGetCurrentUserQuery } from 'services';
import { useIsFree } from 'features/auth';

import styles from './index.module.scss';

interface Props {
	children?: ReactNode;
	candidates: TopIdsWithFiltersCandidates & {
		count: number;
	};
	disabled?: boolean;
	handleExportCsv: (isFull: boolean) => void;
	handleExportGoogleSheets: (isFull: boolean) => void;
	type: 'profiles' | 'companies';
	setProgress: React.Dispatch<React.SetStateAction<'results' | 'export'>>;
}

export const TechnographicsExportToPushMenu: FC<Props> = ({
	children,
	candidates,
	disabled,
	handleExportCsv,
	handleExportGoogleSheets,
	type,
	setProgress
}) => {
	const { data: user } = useGetCurrentUserQuery();

	const isFree = useIsFree();

	const [upgradeModal, setUpgradeModal] = useState(false);
	const [contactUsModal, setContactUsModal] = useState(false);

	const menuDropdown = useModalState();
	const exportToGoogleSheetsModal = useModalState<ExportEnum>();
	const exportToEmailModal = useModalState<ExportEnum>();

	const closeUpgradeModal = () => {
		setProgress('results');
		setUpgradeModal(false);
	};

	const closeContactUsModal = () => {
		setProgress('results');
		setContactUsModal(false);
	};

	const openGoogleSheetsModal = () => {
		if (user && !user.is_google_account) {
			openNotification({ text: 'You need to sign in using Google Account' });
			return;
		}
		if (isFree && user && user.credits < candidates.count) {
			setUpgradeModal(true);
			menuDropdown.close();
			return;
		}
		if (!isFree && user && user.credits < candidates.count) {
			setContactUsModal(true);
			menuDropdown.close();
			return;
		}
		setProgress('export');
		exportToGoogleSheetsModal.open();
		menuDropdown.close();
	};

	const openCsvModal = () => {
		if (isFree && user && user.credits < candidates.count) {
			setUpgradeModal(true);
			menuDropdown.close();
			return;
		}
		if (!isFree && user && user.credits < candidates.count) {
			setContactUsModal(true);
			menuDropdown.close();
			return;
		}
		exportToEmailModal.open();
		setProgress('export');
		menuDropdown.close();
	};

	const handleCloseCsvModal = () => {
		setProgress('results');
		exportToEmailModal.close();
	};

	const handleClosGoogleSheetsModal = () => {
		setProgress('results');
		exportToGoogleSheetsModal.close();
	};

	return (
		<>
			<Dropdown
				disabled={disabled}
				trigger={['click']}
				placement="bottomRight"
				dropdownRender={() => (
					<div className={styles.dropdown}>
						<div className={styles.dropdownList}>
							<button className={styles.button} onClick={openCsvModal}>
								<img className={styles.logo} src={DownloadLogo} alt="Lemlist logo" />
								<Text variant="inter/14/medium" className={styles.text}>
									CSV file
								</Text>
							</button>
							<button className={styles.button} onClick={openGoogleSheetsModal}>
								<img className={styles.logo} src={SheetsLogo} alt="Lemlist logo" />
								<Text variant="inter/14/medium" className={styles.text}>
									Google Sheet
								</Text>
							</button>
						</div>
					</div>
				)}
				onOpenChange={open => (open ? menuDropdown.open() : menuDropdown.close())}
				open={menuDropdown.isOpen}>
				{children}
			</Dropdown>

			{exportToEmailModal.isOpen && (
				<TechnographicsExportCsvModal
					handleExport={handleExportCsv}
					onClose={handleCloseCsvModal}
					candidates={candidates}
					type={type}
				/>
			)}
			{exportToGoogleSheetsModal.isOpen && (
				<ExportGoogleSheetsModal
					isFree={isFree}
					handleExport={handleExportGoogleSheets}
					onClose={handleClosGoogleSheetsModal}
					candidates={candidates}
				/>
			)}
			{upgradeModal && <UpgradeModal onClose={closeUpgradeModal} />}
			{contactUsModal && <ContactUsModal onClose={closeContactUsModal} isExpiring={false} />}
		</>
	);
};
